// Link na bio (pagina propria, separada da vitrine). Leitura via RLS; escrita
// via RPC SECURITY DEFINER (gate PRO). Cliques por link vem de bio_links.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  DEFAULT_BIO_SETTINGS,
  normalizeBioLink,
  normalizeBioSettings,
  type BioLink,
  type BioPageSettings,
} from "@lucralink/shared";
import { supabase } from "@/lib/supabase";
import { mapRpcError } from "@/lib/rpc-error";

export interface BioPageState {
  settings: BioPageSettings;
  status: "draft" | "published";
}

const PAGE_KEY = ["bio-page"] as const;
const LINKS_KEY = ["bio-links"] as const;

const ERRORS: Readonly<Record<string, string>> = {
  bio_requires_pro: "O link na bio é do plano PRO.",
  bio_slug_required: "Defina o endereço (slug) antes de publicar.",
  slug_taken: "Esse endereço já está em uso. Escolha outro.",
  slug_reserved: "Esse endereço é reservado. Escolha outro.",
  slug_invalid: "Endereço inválido: use letras, números e hífens (3 a 40).",
  bio_social_invalid: "Link de rede social inválido: use https.",
  bio_link_url_invalid: "O link precisa começar com https://.",
  bio_link_title_invalid: "Dê um título de 1 a 80 caracteres ao link.",
  bio_links_limit_reached: "Limite de 50 links atingido.",
  logo_url: "Logo inválido: use um endereço https.",
};

export function bioErrorMessage(error: unknown): string {
  return mapRpcError(error, ERRORS, "Não foi possível salvar o link na bio.");
}

export function useBioPage() {
  return useQuery<BioPageState, Error>({
    queryKey: PAGE_KEY,
    queryFn: async () => {
      const { data, error } = await supabase.from("bio_pages").select("*").limit(1).maybeSingle();
      if (error) throw error;
      if (!data) return { settings: DEFAULT_BIO_SETTINGS, status: "draft" };
      const row = data as Record<string, unknown>;
      return { settings: normalizeBioSettings(row), status: row["status"] === "published" ? "published" : "draft" };
    },
    staleTime: 30_000,
  });
}

export function useBioLinks() {
  return useQuery<BioLink[], Error>({
    queryKey: LINKS_KEY,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("bio_links")
        .select("id, title, url, position, is_active, click_count")
        .order("position", { ascending: true });
      if (error) throw error;
      return (data ?? []).map(normalizeBioLink).filter((link): link is BioLink => link !== null);
    },
    staleTime: 15_000,
  });
}

export function useSaveBioPage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { settings: BioPageSettings; status: "draft" | "published"; slug: string | null }) => {
      const socials = Object.fromEntries(Object.entries(input.settings.socials).filter(([, url]) => typeof url === "string" && url.trim().length > 0));
      const { error } = await supabase.rpc("save_bio_page", {
        p_title: input.settings.title,
        p_description: input.settings.description,
        p_logo_url: input.settings.logoUrl,
        p_theme: input.settings.theme,
        p_button_style: input.settings.buttonStyle,
        p_socials: socials,
        p_status: input.status,
        p_slug: input.slug,
      });
      if (error) throw new Error(bioErrorMessage(error));
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: PAGE_KEY });
      await qc.invalidateQueries({ queryKey: ["storefront"] });
    },
  });
}

export function useUpsertBioLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string | null; title: string; url: string; isActive: boolean }) => {
      const { error } = await supabase.rpc("upsert_bio_link", { p_id: input.id, p_title: input.title, p_url: input.url, p_is_active: input.isActive });
      if (error) throw new Error(bioErrorMessage(error));
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: LINKS_KEY }),
  });
}

export function useDeleteBioLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.rpc("delete_bio_link", { p_id: id });
      if (error) throw new Error(bioErrorMessage(error));
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: LINKS_KEY }),
  });
}

export function useReorderBioLinks() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ids: string[]) => {
      const { error } = await supabase.rpc("reorder_bio_links", { p_ids: ids });
      if (error) throw new Error(bioErrorMessage(error));
    },
    onSettled: () => qc.invalidateQueries({ queryKey: LINKS_KEY }),
  });
}
